import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Timestamp } from "firebase/firestore";
import {
  newsService,
  imageUploadService,
  MultilingualString,
} from "../../services/firebaseService";

const NewsForm: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title: { en: "", my: "" } as MultilingualString,
    content: { en: "", my: "" } as MultilingualString,
    excerpt: { en: "", my: "" } as MultilingualString,
    author: "",
    image: "",
    category: "",
    published: false,
    featured: false,
    index: 0,
    customDate: "", // Date string in YYYY-MM-DD format
  });

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string>("");

  useEffect(() => {
    if (isEdit && id) {
      loadNewsItem(id);
    } else {
      // Set default date to today for new items
      const today = new Date().toISOString().split("T")[0];
      setFormData((prev) => ({ ...prev, customDate: today }));
    }
  }, [isEdit, id]);

  const loadNewsItem = async (newsId: string) => {
    try {
      setLoading(true);
      const newsItem = await newsService.getNewsItemById(newsId);
      if (newsItem) {
        // Convert to MultilingualString format if it's a string (backward compatibility)
        const normalizeMultilingual = (
          value: string | MultilingualString
        ): MultilingualString => {
          if (typeof value === "string") {
            return { en: value, my: "" };
          }
          return value;
        };

        // Convert createdAt Timestamp to date string for the input
        const dateString = newsItem.createdAt
          ? newsItem.createdAt.toDate().toISOString().split("T")[0]
          : new Date().toISOString().split("T")[0];

        setFormData({
          title: normalizeMultilingual(newsItem.title),
          content: normalizeMultilingual(newsItem.content),
          excerpt: normalizeMultilingual(newsItem.excerpt),
          author: newsItem.author,
          image: newsItem.image,
          category: newsItem.category,
          published: newsItem.published,
          featured: newsItem.featured,
          index: newsItem.index ?? 0,
          customDate: dateString,
        });
        setImagePreview(newsItem.image);
      }
    } catch (error) {
      console.error("Error loading news item:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select an image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("Image size should be less than 5MB");
        return;
      }

      setImageFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview("");
    setFormData((prev) => ({ ...prev, image: "" }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      let imageUrl = formData.image;

      // Upload image if a new file is selected
      if (imageFile) {
        setUploading(true);
        try {
          imageUrl = await imageUploadService.uploadImage(imageFile, "news");
          setFormData((prev) => ({ ...prev, image: imageUrl }));
        } catch (error) {
          console.error("Error uploading image:", error);
          alert("Failed to upload image. Please try again.");
          setUploading(false);
          setSaving(false);
          return;
        } finally {
          setUploading(false);
        }
      }

      // Convert customDate string to Timestamp
      let customCreatedAt: Timestamp | undefined;
      if (formData.customDate) {
        const date = new Date(formData.customDate);
        // Set time to start of day (00:00:00) for consistency
        date.setHours(0, 0, 0, 0);
        customCreatedAt = Timestamp.fromDate(date);
      }

      // Update formData with the final image URL, excluding customDate
      const { customDate, ...newsData } = formData;
      const finalFormData = { ...newsData, image: imageUrl };

      if (isEdit && id) {
        await newsService.updateNewsItem(id, finalFormData, customCreatedAt);
      } else {
        await newsService.createNewsItem(finalFormData, customCreatedAt);
      }
      navigate("/admin");
    } catch (error) {
      console.error("Error saving news item:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value, type } = e.target;

    // Handle multilingual fields (title.en, title.my, etc.)
    if (name.includes(".")) {
      const [field, lang] = name.split(".");
      if (field === "title" || field === "content" || field === "excerpt") {
        setFormData((prev) => ({
          ...prev,
          [field]: {
            ...(prev[field as keyof typeof prev] as MultilingualString),
            [lang]: value,
          },
        }));
        return;
      }
    }

    setFormData((prev) => ({
      ...prev,
      [name]:
        type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  if (loading) {
    return (
      <div className="admin-panel">
        <div className="loading-container">
          <div className="loading-spinner">
            <div className="spinner"></div>
            <p>Loading news item...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <div className="admin-header">
        <div>
          <h1>{isEdit ? "Edit News Item" : "Create New News Item"}</h1>
          <p>Fill in the details below</p>
        </div>
        <button
          className="btn btn-secondary"
          onClick={() => navigate("/admin")}
        >
          Back to Admin
        </button>
      </div>

      <div className="admin-content">
        <form onSubmit={handleSubmit} className="max-w-[800px] mx-auto">
          <div className="mb-6">
            <label className="block mb-2 font-medium text-gray-700">
              Title *
            </label>
            <div className="space-y-4">
              <div>
                <label className="block mb-1 text-sm text-gray-600">
                  English Title
                </label>
                <input
                  type="text"
                  name="title.en"
                  value={formData.title.en}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                  required
                  placeholder="Enter news title in English"
                />
              </div>
              <div>
                <label className="block mb-1 text-sm text-gray-600">
                  Myanmar Title (မြန်မာ)
                </label>
                <input
                  type="text"
                  name="title.my"
                  value={formData.title.my}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                  required
                  placeholder="Enter news title in Myanmar"
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block mb-2 font-medium text-gray-700">
              Excerpt *
            </label>
            <div className="space-y-4">
              <div>
                <label className="block mb-1 text-sm text-gray-600">
                  English Excerpt
                </label>
                <textarea
                  name="excerpt.en"
                  value={formData.excerpt.en}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 resize-y min-h-[100px]"
                  rows={3}
                  placeholder="Brief description in English"
                  required
                />
              </div>
              <div>
                <label className="block mb-1 text-sm text-gray-600">
                  Myanmar Excerpt (မြန်မာ)
                </label>
                <textarea
                  name="excerpt.my"
                  value={formData.excerpt.my}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 resize-y min-h-[100px]"
                  rows={3}
                  placeholder="Brief description in Myanmar"
                  required
                />
              </div>
            </div>
          </div>

          <div className="mb-6">
            <label className="block mb-2 font-medium text-gray-700">
              Content *
            </label>
            <div className="space-y-4">
              <div>
                <label className="block mb-1 text-sm text-gray-600">
                  English Content
                </label>
                <textarea
                  name="content.en"
                  value={formData.content.en}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 resize-y min-h-[100px]"
                  rows={15}
                  placeholder="Write your news content in English..."
                  required
                />
              </div>
              <div>
                <label className="block mb-1 text-sm text-gray-600">
                  Myanmar Content (မြန်မာ)
                </label>
                <textarea
                  name="content.my"
                  value={formData.content.my}
                  onChange={handleChange}
                  className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10 resize-y min-h-[100px]"
                  rows={15}
                  placeholder="Write your news content in Myanmar..."
                  required
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block mb-2 font-medium text-gray-700">
                Author *
              </label>
              <input
                type="text"
                name="author"
                value={formData.author}
                onChange={handleChange}
                className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                required
                placeholder="Author name"
              />
            </div>

            <div>
              <label className="block mb-2 font-medium text-gray-700">
                Category *
              </label>
              <select
                name="category"
                value={formData.category}
                onChange={handleChange}
                className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
                required
              >
                <option value="">Select Category</option>
                <option value="Events">Events</option>
                <option value="Announcements">Announcements</option>
                <option value="Updates">Updates</option>
                <option value="Partnerships">Partnerships</option>
                <option value="Awards">Awards</option>
              </select>
            </div>
          </div>

          <div className="mb-6">
            <label className="block mb-2 font-medium text-gray-700">
              Date *
            </label>
            <input
              type="date"
              name="customDate"
              value={formData.customDate}
              onChange={handleChange}
              className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              required
            />
            <small className="block mt-2 text-[var(--text-secondary)] text-sm">
              Select the date for this news item. This date will be displayed on the news detail page.
            </small>
          </div>

          <div className="mb-6">
            <label className="block mb-2 font-medium text-gray-700">
              Image *
            </label>
            <input
              type="file"
              name="image"
              accept="image/*"
              onChange={handleImageChange}
              className="w-full px-2 py-2 border-2 border-dashed border-gray-300 rounded-lg text-sm cursor-pointer transition-all duration-200 bg-gray-50 hover:border-primary hover:bg-blue-50/50 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              required={!isEdit || !formData.image}
            />
            {imagePreview && (
              <div className="mt-4 flex flex-col gap-2">
                <div className="relative inline-block max-w-[300px] rounded-lg overflow-hidden border-2 border-gray-200">
                  <img
                    src={imagePreview}
                    alt="Preview"
                    className="w-full h-auto block max-h-[300px] object-contain bg-gray-50"
                  />
                  <button
                    type="button"
                    onClick={handleRemoveImage}
                    className="absolute top-2 right-2 w-8 h-8 rounded-full flex items-center justify-center text-xl leading-none p-0 shadow-lg bg-red-500 text-white hover:scale-110 transition-transform z-10"
                    title="Remove image"
                  >
                    ×
                  </button>
                </div>
                <p className="text-sm text-gray-500 m-0">
                  {imageFile ? `Selected: ${imageFile.name}` : "Current image"}
                </p>
              </div>
            )}
            {!imagePreview && !imageFile && isEdit && formData.image && (
              <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-md text-sm text-blue-900">
                <p className="m-0">
                  Current image will be kept if no new image is selected.
                </p>
              </div>
            )}
          </div>

          <div className="mb-6">
            <label
              htmlFor="index"
              className="block mb-2 font-medium text-gray-700"
            >
              Display Priority (Index)
            </label>
            <input
              type="number"
              id="index"
              name="index"
              value={formData.index}
              onChange={handleChange}
              className="w-full px-3 py-3 border-2 border-gray-200 rounded-lg text-base transition-colors duration-200 focus:outline-none focus:border-primary focus:ring-4 focus:ring-primary/10"
              min="0"
              placeholder="0 = highest priority (appears first)"
            />
            <small className="block mt-2 text-[var(--text-secondary)] text-sm">
              Lower numbers appear first in the news list. News items with the
              same index are sorted by creation date.
            </small>
          </div>

          <div className="flex gap-8 my-6 md:flex-col md:gap-4">
            <label className="flex items-center gap-2 cursor-pointer font-medium text-gray-700">
              <input
                type="checkbox"
                name="published"
                checked={formData.published}
                onChange={handleChange}
              />
              Published
            </label>
            <label className="flex items-center gap-2 cursor-pointer font-medium text-gray-700">
              <input
                type="checkbox"
                name="featured"
                checked={formData.featured}
                onChange={handleChange}
              />
              Featured News
            </label>
          </div>

          <div className="flex gap-4 justify-end mt-8 pt-4 border-t border-gray-200 md:flex-col">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate("/admin")}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={saving || uploading}
            >
              {uploading
                ? "Uploading image..."
                : saving
                ? "Saving..."
                : isEdit
                ? "Update News Item"
                : "Create News Item"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewsForm;
